import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
  Typography,
  Divider,
  InputAdornment,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@material-ui/core";
import { Info, Edit, ArrowForwardIos } from "@material-ui/icons";

import { green } from "@material-ui/core/colors";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "axios";
import { fetchDSBatchDetails } from "../../../containers/actions/common/saslAction";

import {
  Visibility,
  Search as SearchIcon,
  ArrowDropDown,
  Refresh,
  ChevronLeft,
  ChevronRight,
} from "@material-ui/icons";

import { PDFViewer } from "../sasl/batch/updateBatch/StructuredInformation/PDFViewer";
import { appUrl } from "../../../../../utils/config";

// styling
const useStyles = makeStyles((theme) => ({
  formLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: "150px",
    paddingRight: theme.spacing(2),
    textAlign: "right",
  },
  fixedLabel: {
    width: 180,
    textAlign: "left",
    paddingRight: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  requiredStar: {
    color: "red",
    marginLeft: 4,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  testTable: {
    '& .MuiTableCell-head': {
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
    },
  },
}));

const DigitizedCommercialInvoice = (props) => {
  const { initialData, onClose, schrockenCustomFileID, digitizedData, batchDetails, poNumber, batchNumber, onDataRefresh } = props;
  const dispatch = useDispatch();
  const [apiData, setApiData] = useState(initialData || {});
  const [originalData, setOriginalData] = useState(initialData || {});
  const [editedData, setEditedData] = useState(initialData || {});
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [editModalValidationError, setEditModalValidationError] = useState("");
  const [saving, setSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [enableAllFieldsEdit, setEnableAllFieldsEdit] = useState(false);
  const [modalData, setModalData] = useState({
    field: "",
    historyData: [],
    hasHistory: false,
  });
  const [editModalData, setEditModalData] = useState({
    fieldKey: "",
    fieldLabel: "",
    currentValue: "",
    originalValue: "",
    confidenceLevel: 0,
    digitalizationCode: "",
    comment: "",
  });
  const [apiDigitalizationCodes, setApiDigitalizationCodes] = useState([]);

  const classes = useStyles();

  // Helper function to get Commercial Invoice data from digitizedExtractedData
  const getCommercialInvoiceFromExtractedData = () => {
    if (batchDetails?.digitizedExtractedData && Array.isArray(batchDetails.digitizedExtractedData)) {
      const commercialInvoiceDocument = batchDetails.digitizedExtractedData.find(
        doc => doc.documentType === 'CommercialInvoice'
      );
      return commercialInvoiceDocument?.CommericalInvoiceJSONOriginal || null;
    }
    return null;
  };

  // Helper function to check if commercialInvoiceJSONEdited has data
  const hasCommercialInvoiceJSONEditedData = () => {
    if (batchDetails?.commercialInvoiceJSONEdited && typeof batchDetails.commercialInvoiceJSONEdited === 'object') {
      const keys = Object.keys(batchDetails.commercialInvoiceJSONEdited);
      return keys.length > 0;
    }
    return false;
  };

  useEffect(() => {
    let dataToUse = initialData || {};
    let originalDataToUse = initialData || {};

    // First priority: Check if commercialInvoiceJSONEdited has data
    if (hasCommercialInvoiceJSONEditedData()) {
      // console.log('Using commercialInvoiceJSONEdited data');
      dataToUse = batchDetails.commercialInvoiceJSONEdited;
    }

    // Get original unedited data from digitizedExtractedData
    const extractedData = getCommercialInvoiceFromExtractedData();
    if (extractedData) {
      // console.log('Using digitizedExtractedData for original data');
      originalDataToUse = extractedData;
    }

    setApiData(dataToUse);
    setOriginalData(originalDataToUse);
    
    // If we don't have edited data, set the initial data as edited data
    if (!hasCommercialInvoiceJSONEditedData()) {
      setEditedData(dataToUse);
    }
  }, [initialData, batchDetails]);

  // Additional useEffect to handle data refresh after successful API calls
  useEffect(() => {
    // When batchDetails changes (after API refresh), update the local state
    if (batchDetails && !showingOriginal) {
      if (hasCommercialInvoiceJSONEditedData()) {
        setApiData(batchDetails.commercialInvoiceJSONEdited);
        setEditedData(batchDetails.commercialInvoiceJSONEdited);
      }
    }
  }, [batchDetails?.commercialInvoiceJSONEdited, batchDetails?.digitizedExtractedData]);

  // Fetch digitalization codes on component mount
  useEffect(() => {
    const fetchDigitalizationCodes = async () => {
      try {
        const response = await axios.get(`${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/Admin/fetchDigitalizationCodes`);
        // console.log('Digitalization Codes Response:', response.data);
        
        if (response.data.status && response.data.digitalizationCodesList) {
          // Filter only activated codes and map to the format needed for the dropdown
          const activatedCodes = response.data.digitalizationCodesList
            .filter(code => code.activated && !code.markForDelete)
            .map(code => ({
              code: code.digitalizationCode,
              description: code.digitalizationCodeDescription
            }));
          setApiDigitalizationCodes(activatedCodes);
          // console.log('Processed Digitalization Codes:', activatedCodes);
        }
      } catch (error) {
        console.error('Error fetching digitalization codes:', error);
      }
    };

    fetchDigitalizationCodes();
  }, []); // Empty dependency array means this runs once on mount

  // Validation functions based on data types
  const validateField = (fieldKey, value) => {
    const validations = {
      invoice_number: {
        type: "Alphanumeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "CMO Invoice # is required";
          return null;
        }
      },
      invoice_date: {
        type: "Date",
        validate: (val) => {
          if (!val || val.trim() === '') return "CMO Invoice Date is required";
          return null;
        }
      },
      product_code: {
        type: "Numeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "Sponsor SKU is required";
          if (!/^\d+$/.test(val)) return "Must be numeric";
          return null;
        }
      },
      product_description: {
        type: "Text",
        validate: (val) => {
          if (!val || val.trim() === '') return "Sponsor Description is required";
          if (val.length < 5) return "Description should be more detailed";
          return null;
        }
      },
      ready_for_dispatch_quantity: {
        type: "Numeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "Total Shipped Qty is required";
          if (!/^\d+(\.\d+)?$/.test(val.replace(/,/g, ''))) return "Must be a numeric value";
          return null;
        }
      },
      uom: {
        type: "Text",
        validate: (val) => {
          if (!val || val.trim() === '') return "UoM is required";
          return null;
        }
      },
      ship_to: {
        type: "Text",
        validate: (val) => {
          if (!val || val.trim() === '') return "Ship To Location is required";
          if (val.length < 10) return "Address should be more detailed";
          return null;
        }
      }
    };

    const fieldValidation = validations[fieldKey];
    if (fieldValidation) {
      return fieldValidation.validate(value);
    }
    return null;
  };

  const validateAllFields = () => {
    const errors = {};
    fields.forEach(fieldKey => {
      const error = validateField(fieldKey, getValueForField(fieldKey));
      if (error) {
        errors[fieldKey] = error;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getValueForField = (fieldKey) => {
    // If showing original data, get values from digitizedExtractedData
    if (showingOriginal) {
      const originalData = getCommercialInvoiceFromExtractedData();
      if (originalData && originalData[fieldKey]) {
        const fieldData = originalData[fieldKey];
        if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
          return fieldData.value || '';
        }
        return fieldData || '';
      }
      return '';
    }
    
    // Otherwise use the current apiData (which is edited data)
    const fieldData = apiData[fieldKey];
    if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
      return fieldData.value || '';
    }
    return fieldData || '';
  };

  // Helper function to check if field should show info icon for edited data with reason/comment
  const shouldShowInfoIcon = (fieldKey) => {
    // Don't show info icon when showing original data
    if (showingOriginal) return false;
    
    if (!hasCommercialInvoiceJSONEditedData() || !batchDetails?.commercialInvoiceJSONEdited?.[fieldKey]) return false;
    
    const fieldData = batchDetails.commercialInvoiceJSONEdited[fieldKey];
    return fieldData.needValidation === false && (fieldData.reasonCode || fieldData.comment);
  };

  // Helper function to check if field has needValidation: true
  const shouldShowEditForValidation = (fieldKey) => {
    // Don't show edit icon when showing original data
    if (showingOriginal) return false;
    
    // Check edited data first, then fall back to original data
    const currentCommercialInvoiceData = batchDetails?.commercialInvoiceJSONEdited || getCommercialInvoiceFromExtractedData();
    const fieldData = currentCommercialInvoiceData?.[fieldKey];
    if (!fieldData || typeof fieldData !== 'object') return false;
    
    return fieldData.needValidation === true;
  };

  // Helper function to check if editing should be disabled based on needValidation flags
  const isEditingDisabled = () => {
    // Get current Commercial Invoice data (edited or original)
    const currentCommercialInvoiceData = batchDetails?.commercialInvoiceJSONEdited || getCommercialInvoiceFromExtractedData();
    if (!currentCommercialInvoiceData) return true;
    
    // Define field keys for Commercial Invoice
    const fieldKeys = ['invoice_number', 'invoice_date', 'product_code', 'product_description', 'batch', 'ready_for_dispatch_quantity', 'uom', 'ship_to'];
    
    // Check if any field has needValidation: true
    const hasFieldsNeedingValidation = fieldKeys.some(fieldKey => {
      const fieldData = currentCommercialInvoiceData[fieldKey];
      return fieldData && typeof fieldData === 'object' && fieldData.needValidation === true;
    });
    
    // Enable editing if any field has needValidation: true
    return !hasFieldsNeedingValidation;
  };

  // Function to fetch field history from API
  const fetchFieldHistory = async (fieldKey, fieldLabel) => {
    try {
      setOpenModal(false); // Close any existing modal
      setIsFetchingHistory(true); // Set loading state
      
      const requestPayload = {
        schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
        documentType: "CommercialInvoice",
        dataFieldName: fieldKey
      };

      // console.log('Fetching field history for:', fieldKey, requestPayload);

      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/fetchExtractedDataHistory`,
        requestPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // console.log('Field history response:', response.data);

      if (response.data?.status && response.data?.fieldDataHistory && response.data.fieldDataHistory.length > 0) {
        // console.log(`Found ${response.data.fieldDataHistory.length} history records for field: ${fieldKey}`);
        // Use all history data from API
        const historyRecords = response.data.fieldDataHistory.map(record => ({
          before: record.beforeValue || 'N/A',
          after: record.afterValue || 'N/A',
          updatedBy: record.lastUpdatedBy || 'System',
          timestamp: record.lastUpdatedDate ? new Date(record.lastUpdatedDate).toLocaleString() : new Date().toLocaleString(),
        }));
        
        setModalData({
          field: fieldLabel,
          historyData: historyRecords,
          hasHistory: true,
        });
      } else {
        // No history found - show "No history found" message
        setModalData({
          field: fieldLabel,
          historyData: [],
          hasHistory: false,
        });
      }

      setOpenModal(true);
    } catch (error) {
      console.error('Error fetching field history:', error);
      
      // Show "No history found" on error as well
      setModalData({
        field: fieldLabel,
        historyData: [],
        hasHistory: false,
      });
      setOpenModal(true);
    } finally {
      setIsFetchingHistory(false); // Reset loading state
    }
  };

  const getConfidenceForField = (fieldKey) => {
    // If showing original data, get confidence from digitizedExtractedData
    if (showingOriginal) {
      const originalData = getCommercialInvoiceFromExtractedData();
      if (originalData && originalData[fieldKey]) {
        const fieldData = originalData[fieldKey];
        if (fieldData && typeof fieldData === 'object' && fieldData.confidence != null) {
          return parseFloat(fieldData.confidence);
        }
        // If field exists but has no value, return 0
        if (fieldData && typeof fieldData === 'object' && (!fieldData.value || fieldData.value.trim() === '')) {
          return 0;
        }
      }
      // If field doesn't exist in response, return 0
      return 0;
    }
    
    // Otherwise use the current apiData (which is edited data)
    const fieldData = apiData[fieldKey];
    if (fieldData && typeof fieldData === 'object' && fieldData.confidence != null) {
      return parseFloat(fieldData.confidence);
    }
    
    // If field exists but has no value, return 0
    if (fieldData && typeof fieldData === 'object' && (!fieldData.value || fieldData.value.trim() === '')) {
      return 0;
    }
    
    // For fields that don't have confidence data or don't exist in the API response, return 0
    return 0;
  };

  const getLevelColor = (level) => {
    if (level === null || level === undefined) return "#cccccc"; // Gray for N/A
    
    // Get dynamic confidence levels from batch details
    const confidenceLevels = batchDetails?.confidenceLevels;
    const lowConfidenceLimit = confidenceLevels?.lowConfidenceLimit || 80;
    const highConfidenceLimit = confidenceLevels?.highConfidenceLimit || 90;
    
    if (level < lowConfidenceLimit) return "red";
    if (level <= highConfidenceLimit) return "orange";
    return "green";
  };

  const getConfidenceDisplay = (fieldKey) => {
    const confidence = getConfidenceForField(fieldKey);
    return confidence !== null ? Math.round(confidence) : "0";
  };

  const handleInputChange = (apiField, value) => {
    const currentFieldData = apiData[apiField];
    const newData = {
      ...apiData,
      [apiField]: typeof currentFieldData === 'object' ? 
        { ...currentFieldData, value: value } : 
        value,
    };
    setApiData(newData);
    if (!showingOriginal) {
      setEditedData(newData);
    }

    // Validate the field and update errors
    const error = validateField(apiField, value);
    setValidationErrors(prev => ({
      ...prev,
      [apiField]: error
    }));
  };

  // Digitalization code options
  // Use API data if available, otherwise use empty array
  const digitalizationCodes = apiDigitalizationCodes;

  // Field mapping from API to display labels
  const fieldMapping = {
    invoice_number: "CMO Invoice #",
    invoice_date: "CMO Invoice Date",
    product_code: "Sponsor SKU",
    product_description: "Sponsor Description",
    batch: "Batch Number",
    ready_for_dispatch_quantity: "Total Shipped Qty",
    uom: "UoM",
    ship_to: "Ship To Location"
  };

  const fields = Object.keys(fieldMapping);

  const handleOpenEditModal = (fieldKey, fieldLabel) => {
    const currentValue = getValueForField(fieldKey);
    const confidence = getConfidenceForField(fieldKey);
    
    setEditModalData({
      fieldKey,
      fieldLabel,
      currentValue,
      originalValue: currentValue, // Store the original value
      confidenceLevel: confidence,
      digitalizationCode: "",
      comment: "",
    });
    setEditModalValidationError(""); // Clear any previous validation errors
    setOpenEditModal(true);
  };

  const handleSaveFieldEdit = async () => {
    // console.log('=== handleSaveFieldEdit started ===');
    // console.log('editModalData:', editModalData);
    // console.log('batchDetails:', batchDetails);
    
    // Allow empty values - removed validation that prevented empty values
    
    // Validation: if no digitalization code is selected, comment is mandatory
    if (!editModalData.digitalizationCode && !editModalData.comment.trim()) {
      setEditModalValidationError("If reason code is not entered, comment field is mandatory");
      return;
    }

    // Clear validation error if validation passes
    setEditModalValidationError("");

    try {
      setSaving(true);

      // Update the current data
      const currentFieldData = apiData[editModalData.fieldKey];
      const newData = {
        ...apiData,
        [editModalData.fieldKey]: typeof currentFieldData === 'object' ? 
          { 
            ...currentFieldData, 
            value: editModalData.currentValue,
            needValidation: false,
            reasonCode: editModalData.digitalizationCode,
            comment: editModalData.comment
          } : 
          {
            value: editModalData.currentValue,
            confidence: editModalData.confidenceLevel,
            needValidation: false,
            reasonCode: editModalData.digitalizationCode,
            comment: editModalData.comment
          },
      };
      setApiData(newData);
      setEditedData(newData);

      // Check if the field being edited had needValidation: true
      const wasFieldNeedingValidation = apiData[editModalData.fieldKey] && 
        apiData[editModalData.fieldKey].needValidation === true;

      // Prepare updated Commercial Invoice JSON with all current field values
      const updatedCommercialInvoiceJSON = {};
      
      // Add all fields from the current data
      fields.forEach(fieldKey => {
        if (fieldKey === editModalData.fieldKey) {
          // Use the edited value for the current field
          updatedCommercialInvoiceJSON[fieldKey] = {
            value: editModalData.currentValue,
            confidence: editModalData.confidenceLevel,
            needValidation: false,
            reasonCode: editModalData.digitalizationCode,
            comment: editModalData.comment
          };
        } else {
          // Use existing values for other fields
          const fieldData = apiData[fieldKey];
          if (fieldData && typeof fieldData === 'object') {
            updatedCommercialInvoiceJSON[fieldKey] = {
              value: fieldData.value,
              confidence: fieldData.confidence,
              ...(fieldData.needValidation !== undefined && { needValidation: fieldData.needValidation }),
              ...(fieldData.reasonCode && { reasonCode: fieldData.reasonCode }),
              ...(fieldData.comment && { comment: fieldData.comment })
            };
          } else {
            updatedCommercialInvoiceJSON[fieldKey] = {
              value: fieldData || '',
              confidence: null
            };
          }
        }
      });

      // Add metadata fields and update review counters
    const existingData = apiData;
      if (existingData.document_type) updatedCommercialInvoiceJSON.document_type = existingData.document_type;
      if (existingData.folder_name) updatedCommercialInvoiceJSON.folder_name = existingData.folder_name;
      if (existingData.site_name) updatedCommercialInvoiceJSON.site_name = existingData.site_name;
      if (existingData.filename) updatedCommercialInvoiceJSON.filename = existingData.filename;
      if (existingData.s3_location) updatedCommercialInvoiceJSON.s3_location = existingData.s3_location;
      
      // Update review counters if a field with needValidation: true was edited
      let noOfFieldsToBeReviewed = existingData.noOfFieldsToBeReviewed || 0;
      let noOfFieldsReviewed = existingData.noOfFieldsReviewed || 0;
      
      if (wasFieldNeedingValidation) {
        // Increment reviewed count and decrement to-be-reviewed count
        noOfFieldsReviewed = noOfFieldsReviewed + 1;
        noOfFieldsToBeReviewed = Math.max(0, noOfFieldsToBeReviewed - 1);
        
        // console.log(`Field ${editModalData.fieldKey} with needValidation=true was edited. Updated counters:`, {
        //   noOfFieldsReviewed,
        //   noOfFieldsToBeReviewed
        // });
      }
      
      updatedCommercialInvoiceJSON.noOfFieldsToBeReviewed = noOfFieldsToBeReviewed;
      updatedCommercialInvoiceJSON.noOfFieldsReviewed = noOfFieldsReviewed;

      // console.log('updatedCommercialInvoiceJSON:', updatedCommercialInvoiceJSON);

      // Prepare API request payload for handleDataExtractionEditedData
      const requestPayload = {
        schrockenDeliveryScheduleID: batchDetails?.schrockenDeliveryScheduleID,
        schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
        commercialInvoiceJSONEdited: updatedCommercialInvoiceJSON,
        poNumber: poNumber,
        batchNumber: batchNumber,
        cdmoOrgID: batchDetails?.vendorMosymphonyID,
        file_type: digitizedData?.documentType || "CertificateOfManufacture",
      };

      // console.log('requestPayload:', requestPayload);

      // Make API call
      const response = await axios.post(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/workflow/handleDataExtractionEditedData`,
        requestPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // console.log('API response:', response.data);

      if (response.data.success && response.data.cordaResponse?.status) {
        // console.log('Commercial Invoice field saved successfully');
        
        // Close the edit modal first
        setOpenEditModal(false);
        
        // Refresh batch details to get updated data
        if (onDataRefresh) {
          setIsRefreshing(true);
          try {
            await onDataRefresh();
          } finally {
            setIsRefreshing(false);
          }
        } else {
          // Fallback: dispatch fetchDSBatchDetails
          const refreshData = {
            schrockenDeliveryScheduleBatchID: batchDetails?.schrockenDeliveryScheduleBatchID,
          };
          dispatch(fetchDSBatchDetails(refreshData));
        }
        
        // Close the main popup/dialog if onClose is available
        if (onClose) {
          // Add a small delay to ensure refresh completes
          setTimeout(() => {
            onClose();
          }, 500);
        }
      } else {
        console.error('Failed to save:', response.data.message || 'Unknown error');
        // You might want to show an error message to the user here
      }
    } catch (error) {
      console.error('Error saving Commercial Invoice field:', error);
      // You might want to show an error message to the user here
    } finally {
      setSaving(false);
    }
  };

  const isFieldChanged = (apiField) => {
    const currentValue = getValueForField(apiField);
    const originalValue = originalData[apiField];
    const originalValueToCompare = originalValue && typeof originalValue === 'object' && originalValue.value !== undefined
      ? originalValue.value 
      : originalValue;
    
    return currentValue !== originalValueToCompare;
  };

  const getFieldErrorProps = (fieldKey) => ({
    error: !!validationErrors[fieldKey],
    helperText: validationErrors[fieldKey]
  });

  return (
    <>
      {/* Loading overlay when fetching field history */}
      {isFetchingHistory && (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box 
            style={{
              backgroundColor: 'white',
              padding: 24,
              borderRadius: 8,
              textAlign: 'center',
              minWidth: 200,
            }}
          >
            <CircularProgress size={40} style={{ marginBottom: 16 }} />
            <Typography variant="h6">Loading field history...</Typography>
          </Box>
        </Box>
      )}

      {/* Loading overlay when refreshing data */}
      {isRefreshing && (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box 
            style={{
              backgroundColor: 'white',
              padding: 24,
              borderRadius: 8,
              textAlign: 'center',
              minWidth: 200,
            }}
          >
            <CircularProgress size={40} style={{ marginBottom: 16 }} />
            <Typography variant="h6">Refreshing data...</Typography>
          </Box>
        </Box>
      )}
      
      <Grid container direction="column" spacing={2}>
        <Grid item container alignItems="center" spacing={2}>
          <Grid item>
            <Button
              startIcon={<ChevronLeft fontSize="small" />}
              style={{
                fontWeight: "bold",
                textTransform: "none",
                marginLeft: "0px",
              }}
              onClick={onClose}
            >
              Back
            </Button>
          </Grid>
          <Grid item>
            <Typography variant="h6">
              Digitized Commercial Invoice
            </Typography>
          </Grid>
        </Grid>

        <Grid item>
          <Divider
            style={{
              margin: "0px 0px 0px 0px",
              backgroundColor: "#707070",
              height: 1,
              zIndex: 4,
            }}
          />
        </Grid>

        {/* Actions*/}
        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <Grid item style={{display: "flex", justifyContent: "space-between", alignItems: "center"}} spacing={2}>
              <Typography
                variant="h6"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  marginRight: "8px",
                }}
              >
                Commercial Invoice Data
              </Typography>
              <Box display="flex" gap={1} spacing={2}>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    if (showingOriginal) {
                      setApiData(editedData);
                      setShowingOriginal(false);
                    } else {
                      setApiData(originalData);
                      setShowingOriginal(true);
                    }
                  }}
                  style={{ marginRight: "8px", minWidth: 20, textAlign: "center", size: "20px" }}
                >
                  {showingOriginal ? "Show Edited Version" : "Show Unedited Version"}
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">PDF Preview</Typography>
          </Grid>
        </Grid>

        

        <Grid item container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              {/* Basic Information Fields */}
              
              {/* CMO Invoice # Field */}
              <Grid container alignItems="center" spacing={2} className={classes.formField}>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                      CMO Invoice #
                      <span className={classes.requiredStar}>*</span>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box style={{ width: "calc(100% - 70px)" }}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={getValueForField("invoice_number")}
                        placeholder="Enter CMO Invoice #"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box display="flex" alignItems="center" pl={1}>
                                <Box style={{ width: 1, height: 39, backgroundColor: "#ccc", marginRight: 3 }} />
                                {shouldShowInfoIcon("invoice_number") ? (
                                  <Box
                                    onClick={() => fetchFieldHistory("invoice_number", "CMO Invoice #")}
                                    style={{
                                      backgroundColor: "#777",
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Info fontSize="small" />
                                  </Box>
                                ) : (
                                  <Box style={{
                                    backgroundColor: getLevelColor(getConfidenceForField("invoice_number")),
                                    color: "white",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    minWidth: 24,
                                    textAlign: "center",
                                  }}>
                                    {getConfidenceDisplay("invoice_number")}
                                  </Box>
                                )}
                              </Box>
                            </InputAdornment>
                          ),
                          ...getFieldErrorProps("invoice_number")
                        }}
                      />
                    </Box>
                    <Box style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {(enableAllFieldsEdit || shouldShowInfoIcon("invoice_number") || shouldShowEditForValidation("invoice_number")) && !showingOriginal && !isEditingDisabled() ? (
                        <Box
                          onClick={() => handleOpenEditModal("invoice_number", "CMO Invoice #")}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Edit fontSize="small" style={{ color: "#EC6535" }} />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* CMO Invoice Date Field */}
              <Grid container alignItems="center" spacing={2} className={classes.formField}>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                      CMO Invoice Date
                      <span className={classes.requiredStar}>*</span>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box style={{ width: "calc(100% - 70px)" }}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={getValueForField("invoice_date")}
                        onChange={(e) => handleInputChange("invoice_date", e.target.value)}
                        placeholder="Enter CMO Invoice Date"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box display="flex" alignItems="center" pl={1}>
                                <Box style={{ width: 1, height: 39, backgroundColor: "#ccc", marginRight: 3 }} />
                                {shouldShowInfoIcon("invoice_date") ? (
                                  <Box
                                    onClick={() => fetchFieldHistory("invoice_date", "CMO Invoice Date")}
                                    style={{
                                      backgroundColor: "#777",
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Info fontSize="small" />
                                  </Box>
                                ) : (
                                  <Box style={{
                                    backgroundColor: getLevelColor(getConfidenceForField("invoice_date")),
                                    color: "white",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    minWidth: 24,
                                    textAlign: "center",
                                  }}>
                                    {getConfidenceDisplay("invoice_date")}
                                  </Box>
                                )}
                              </Box>
                            </InputAdornment>
                          ),
                          ...getFieldErrorProps("invoice_date")
                        }}
                      />
                    </Box>
                    <Box style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {(enableAllFieldsEdit || shouldShowInfoIcon("invoice_date") || shouldShowEditForValidation("invoice_date")) && !showingOriginal && !isEditingDisabled() ? (
                        <Box
                          onClick={() => handleOpenEditModal("invoice_date", "CMO Invoice Date")}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Edit fontSize="small" style={{ color: "#EC6535" }} />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Sponsor SKU Field */}
              <Grid container alignItems="center" spacing={2} className={classes.formField}>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                      Sponsor SKU
                      {/* <span className={classes.requiredStar}>*</span> */}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box style={{ width: "calc(100% - 70px)" }}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={getValueForField("product_code")}
                        onChange={(e) => handleInputChange("product_code", e.target.value)}
                        placeholder="Enter Sponsor SKU"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box display="flex" alignItems="center" pl={1}>
                                <Box style={{ width: 1, height: 39, backgroundColor: "#ccc", marginRight: 3 }} />
                                {shouldShowInfoIcon("product_code") ? (
                                  <Box
                                    onClick={() => fetchFieldHistory("product_code", "Sponsor SKU")}
                                    style={{
                                      backgroundColor: "#777",
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Info fontSize="small" />
                                  </Box>
                                ) : (
                                  <Box style={{
                                    backgroundColor: getLevelColor(getConfidenceForField("product_code")),
                                    color: "white",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    minWidth: 24,
                                    textAlign: "center",
                                  }}>
                                    {getConfidenceDisplay("product_code")}
                                  </Box>
                                )}
                              </Box>
                            </InputAdornment>
                          ),
                          ...getFieldErrorProps("product_code")
                        }}
                      />
                    </Box>
                    <Box style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {(enableAllFieldsEdit || shouldShowInfoIcon("product_code") || shouldShowEditForValidation("product_code")) && !showingOriginal && !isEditingDisabled() ? (
                        <Box
                          onClick={() => handleOpenEditModal("product_code", "Sponsor SKU")}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Edit fontSize="small" style={{ color: "#EC6535" }} />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Sponsor Description Field */}
              <Grid container alignItems="center" spacing={2} className={classes.formField}>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                      Sponsor Description
                      {/* <span className={classes.requiredStar}>*</span> */}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <Box style={{ width: "calc(100% - 70px)" }}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={getValueForField("product_description")}
                        onChange={(e) => handleInputChange("product_description", e.target.value)}
                        placeholder="Enter Sponsor Description"
                        multiline
                        rows={2}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box display="flex" alignItems="center" pl={1}>
                                <Box style={{ width: 1, height: 50, backgroundColor: "#ccc", marginRight: 3 }} />
                                {shouldShowInfoIcon("product_description") ? (
                                  <Box
                                    onClick={() => fetchFieldHistory("product_description", "Sponsor Description")}
                                    style={{
                                      backgroundColor: "#777",
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Info fontSize="small" />
                                  </Box>
                                ) : (
                                  <Box style={{
                                    backgroundColor: getLevelColor(getConfidenceForField("product_description")),
                                    color: "white",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    minWidth: 24,
                                    textAlign: "center",
                                  }}>
                                    {getConfidenceDisplay("product_description")}
                                  </Box>
                                )}
                              </Box>
                            </InputAdornment>
                          ),
                          ...getFieldErrorProps("product_description")
                        }}
                      />
                    </Box>
                    <Box style={{ width: 32, height: 32, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 4 }}>
                      {(enableAllFieldsEdit || shouldShowInfoIcon("product_description") || shouldShowEditForValidation("product_description")) && !showingOriginal && !isEditingDisabled() ? (
                        <Box
                          onClick={() => handleOpenEditModal("product_description", "Sponsor Description")}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Edit fontSize="small" style={{ color: "#EC6535" }} />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Total Shipped Qty Field */}
              <Grid container alignItems="center" spacing={2} className={classes.formField}>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                      Total Shipped Qty
                      <span className={classes.requiredStar}>*</span>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box style={{ width: "calc(100% - 70px)" }}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={getValueForField("ready_for_dispatch_quantity")}
                        onChange={(e) => handleInputChange("ready_for_dispatch_quantity", e.target.value)}
                        placeholder="Enter Total Shipped Qty"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box display="flex" alignItems="center" pl={1}>
                                <Box style={{ width: 1, height: 39, backgroundColor: "#ccc", marginRight: 3 }} />
                                {shouldShowInfoIcon("ready_for_dispatch_quantity") ? (
                                  <Box
                                    onClick={() => fetchFieldHistory("ready_for_dispatch_quantity", "Total Shipped Qty")}
                                    style={{
                                      backgroundColor: "#777",
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Info fontSize="small" />
                                  </Box>
                                ) : (
                                  <Box style={{
                                    backgroundColor: getLevelColor(getConfidenceForField("ready_for_dispatch_quantity")),
                                    color: "white",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    minWidth: 24,
                                    textAlign: "center",
                                  }}>
                                    {getConfidenceDisplay("ready_for_dispatch_quantity")}
                                  </Box>
                                )}
                              </Box>
                            </InputAdornment>
                          ),
                          ...getFieldErrorProps("ready_for_dispatch_quantity")
                        }}
                      />
                    </Box>
                    <Box style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {(enableAllFieldsEdit || shouldShowInfoIcon("ready_for_dispatch_quantity") || shouldShowEditForValidation("ready_for_dispatch_quantity")) && !showingOriginal && !isEditingDisabled() ? (
                        <Box
                          onClick={() => handleOpenEditModal("ready_for_dispatch_quantity", "Total Shipped Qty")}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Edit fontSize="small" style={{ color: "#EC6535" }} />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* UoM Field */}
              <Grid container alignItems="center" spacing={2} className={classes.formField}>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                      UoM
                      <span className={classes.requiredStar}>*</span>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box style={{ width: "calc(100% - 70px)" }}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={getValueForField("uom")}
                        onChange={(e) => handleInputChange("uom", e.target.value)}
                        placeholder="Enter UoM"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box display="flex" alignItems="center" pl={1}>
                                <Box style={{ width: 1, height: 39, backgroundColor: "#ccc", marginRight: 3 }} />
                                {shouldShowInfoIcon("uom") ? (
                                  <Box
                                    onClick={() => fetchFieldHistory("uom", "UoM")}
                                    style={{
                                      backgroundColor: "#777",
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Info fontSize="small" />
                                  </Box>
                                ) : (
                                  <Box style={{
                                    backgroundColor: getLevelColor(getConfidenceForField("uom")),
                                    color: "white",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    minWidth: 24,
                                    textAlign: "center",
                                  }}>
                                    {getConfidenceDisplay("uom")}
                                  </Box>
                                )}
                              </Box>
                            </InputAdornment>
                          ),
                          ...getFieldErrorProps("uom")
                        }}
                      />
                    </Box>
                    <Box style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {(enableAllFieldsEdit || shouldShowInfoIcon("uom") || shouldShowEditForValidation("uom")) && !showingOriginal && !isEditingDisabled() ? (
                        <Box
                          onClick={() => handleOpenEditModal("uom", "UoM")}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Edit fontSize="small" style={{ color: "#EC6535" }} />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Ship To Location Field */}
              <Grid container alignItems="center" spacing={2} className={classes.formField}>
                <Grid item xs={4}>
                  <Box display="flex" alignItems="center">
                    <Typography style={{ paddingRight: 16, fontWeight: 500 }}>
                      Ship To Location
                      <span className={classes.requiredStar}>*</span>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <Box style={{ width: "calc(100% - 70px)" }}>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={getValueForField("ship_to")}
                        onChange={(e) => handleInputChange("ship_to", e.target.value)}
                        placeholder="Enter Ship To Location"
                        multiline
                        rows={3}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Box display="flex" alignItems="center" pl={1}>
                                <Box style={{ width: 1, height: 70, backgroundColor: "#ccc", marginRight: 3 }} />
                                {shouldShowInfoIcon("ship_to") ? (
                                  <Box
                                    onClick={() => fetchFieldHistory("ship_to", "Ship To Location")}
                                    style={{
                                      backgroundColor: "#777",
                                      color: "white",
                                      borderRadius: 4,
                                      padding: "2px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      minWidth: 24,
                                      textAlign: "center",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Info fontSize="small" />
                                  </Box>
                                ) : (
                                  <Box style={{
                                    backgroundColor: getLevelColor(getConfidenceForField("ship_to")),
                                    color: "white",
                                    borderRadius: 4,
                                    padding: "2px 8px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    minWidth: 24,
                                    textAlign: "center",
                                  }}>
                                    {getConfidenceDisplay("ship_to")}
                                  </Box>
                                )}
                              </Box>
                            </InputAdornment>
                          ),
                          ...getFieldErrorProps("ship_to")
                        }}
                      />
                    </Box>
                    <Box style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {(enableAllFieldsEdit || shouldShowInfoIcon("ship_to") || shouldShowEditForValidation("ship_to")) && !showingOriginal && !isEditingDisabled() ? (
                        <Box
                          onClick={() => handleOpenEditModal("ship_to", "Ship To Location")}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginTop: 4,
                          }}
                        >
                          <Edit fontSize="small" style={{ color: "#EC6535" }} />
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                </Grid>
              </Grid>

            

              <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
                <Typography variant="body2" color="textSecondary">
                  Confidence Levels Legend:{" "}
                </Typography>
                <Grid item style={{ width: "28px" }} />
                {(() => {
                  // Get dynamic confidence levels from batch details
                  const confidenceLevels = batchDetails?.confidenceLevels;
                  const lowConfidenceLimit = confidenceLevels?.lowConfidenceLimit || 80;
                  const highConfidenceLimit = confidenceLevels?.highConfidenceLimit || 90;
                  
                  return (
                    <>
                      {/* Green Box */}
                      <Box
                        style={{
                          backgroundColor: "green",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 30,
                        }}
                      >
                        &gt; {highConfidenceLimit}
                      </Box>
                      {/* Orange Box */}
                      <Box
                        style={{
                          backgroundColor: "orange",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 50,
                          marginLeft: 16,
                        }}
                      >
                        {confidenceLevels?.mediumConfidenceLimitMinValue} - {confidenceLevels?.mediumConfidenceLimitMaxValue}
                      </Box>
                      {/* Red Box */}
                      <Box
                        style={{
                          backgroundColor: "red",
                          color: "white",
                          borderRadius: 4,
                          padding: "4px 12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textAlign: "center",
                          minWidth: 30,
                          marginLeft: 16,
                        }}
                      >
                        &lt; {lowConfidenceLimit}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </Paper>
          </Grid>

          {/* Right side: PDF view */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              <Box
                style={{
                  height: 500,
                  backgroundColor: "#f5f5f5",
                  border: "1px dashed #ccc",
                }}
              >
                {schrockenCustomFileID ? (
                  <PDFViewer schrockenCustomFileID={schrockenCustomFileID} />
                ) : (
                  <Box
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography>No PDF available</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Edit All Fields Option - only show when not showing original */}
        {!showingOriginal && (
          <Grid item xs={12}>
            <Box 
              style={{
                border: "1px solid #ddd",
                padding: "12px",
                borderRadius: "4px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableAllFieldsEdit}
                    onChange={(e) => setEnableAllFieldsEdit(e.target.checked)}
                    color="primary"
                    disabled={isEditingDisabled()}
                  />
                }
                label="Apply edit feature to all the fields"
              />
              <Typography 
                variant="body2" 
                style={{ 
                  fontStyle: "italic", 
                  color: "#666", 
                  marginTop: "4px" 
                }}
              >
                Note - This will enable editing of the high confidence level fields, highlighted in green colors.
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: 24,
            boxShadow: 24,
            borderRadius: 8,
            minWidth: 600,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" gutterBottom>
            {modalData.field} - Field Data History 
            {/* {modalData.hasHistory && modalData.historyData.length > 0 ? `(${modalData.historyData.length} record${modalData.historyData.length !== 1 ? 's' : ''})` : ''} */}
          </Typography>

          <Box style={{ maxHeight: "400px", overflowY: "auto" }}>
            <Table>
              <TableHead
                style={{ border: "1px solid grey", backgroundColor: "#7777" }}
              >
                <TableRow
                  style={{ border: "1px solid grey", backgroundColor: "#7777" }}
                >
                  <TableCell>
                    <b>Timestamp</b>
                  </TableCell>
                  <TableCell>
                    <b>Before Value</b>
                  </TableCell>
                  <TableCell>
                    <b>After Value</b>
                  </TableCell>
                  <TableCell>
                    <b>Updated By</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody style={{ border: "1px solid grey" }}>
                {modalData.hasHistory ? (
                  modalData.historyData.map((record, index) => (
                    <TableRow key={index}>
                      <TableCell>{record.timestamp}</TableCell>
                      <TableCell>{record.before}</TableCell>
                      <TableCell>{record.after}</TableCell>
                      <TableCell>{record.updatedBy}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                      No history found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(false)}
              style={{borderRadius: 30}}
              endIcon={<ArrowForwardIos style={{ fontSize: 15 }} />}
            >
              Ok
            </Button>
          </Box>
        </Box>
      </Modal>

             <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
          <Box
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "#fff",
              padding: 24,
              boxShadow: 24,
              borderRadius: 8,
              minWidth: 600,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Edit {editModalData.fieldLabel} for PO {poNumber || ''}
              </Typography>
              <Button onClick={() => setOpenEditModal(false)} style={{ minWidth: 'auto' }}>
                ✕
              </Button>
            </Box>
 
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold' }}>
                  {editModalData.fieldLabel}
                </Typography>
                <Box display="flex" alignItems="center">
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={editModalData.currentValue}
                  onChange={(e) => setEditModalData(prev => ({ ...prev, currentValue: e.target.value }))}
                  onBlur={(e) => {
                    const { value } = e.target;
                    // Trim leading and trailing spaces for string values
                    if (typeof value === "string" && value.trim() !== value) {
                      setEditModalData(prev => ({ ...prev, currentValue: value.trim() }));
                    }
                  }}
                  />
                  {((editModalData.confidenceLevel !== null && editModalData.confidenceLevel > 90) || shouldShowInfoIcon(editModalData.fieldKey)) ? null : (
                    <Box
                      onClick={() => {
                        if (editModalData.currentValue === editModalData.originalValue) {
                          setEditModalData(prev => ({ ...prev, digitalizationCode: "DG001" }))
                        }
                      }}
                      style={{
                        backgroundColor: editModalData.currentValue === editModalData.originalValue ? "green" : "#cccccc",
                        color: "white",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        minWidth: 24,
                        textAlign: "center",
                        cursor: editModalData.currentValue === editModalData.originalValue ? "pointer" : "not-allowed",
                        opacity: editModalData.currentValue === editModalData.originalValue ? 1 : 0.5,
                        marginLeft: 8,
                      }}
                    >
                      ✓
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold' }}>
                  Confidence Level (%)
                </Typography>
                <Box
                 
                >
                  <Typography variant="body2">
                    {editModalData.confidenceLevel !== null ? Math.round(editModalData.confidenceLevel) : "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold' }}>
                  Digitalization Code / Description
                </Typography>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={editModalData.digitalizationCode}
                    onChange={(e) => {
                      setEditModalData(prev => ({ ...prev, digitalizationCode: e.target.value }));
                      setEditModalValidationError(""); // Clear validation error when user selects a code
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select a digitalization code</em>
                    </MenuItem>
                    {digitalizationCodes.map((code) => (
                      <MenuItem key={code.code} value={code.code}>
                        {code.code} / {code.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom style={{ fontWeight: 'bold' }}>
                  Comment
                </Typography>
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="Enter text here"
                  value={editModalData.comment}
                  onChange={(e) => {
                    if(e.target.value.length < 256){
                    setEditModalData(prev => ({ ...prev, comment: e.target.value }));
                    }
                    setEditModalValidationError(""); // Clear validation error when user types in comment
                  }}
                  onBlur={(e) => {
                    const { value } = e.target;
                    // Trim leading and trailing spaces for string values
                    if (typeof value === "string" && value.trim() !== value) {
                      setEditModalData(prev => ({ ...prev, comment: value.trim() }));
                    }
                  }}
                  multiline
                  rows={3}
                />
                {editModalValidationError && (
                  <Typography 
                    variant="body2" 
                    color="error" 
                    style={{ 
                      marginTop: 8, 
                      padding: "8px 12px",
                      backgroundColor: "#ffebee",
                      border: "1px solid #f44336",
                      borderRadius: 4
                    }}
                  >
                    {editModalValidationError}
                  </Typography>
                )}
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                  Note - If reason code is not entered, comment field is mandatory
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="contained"
                    style={{borderRadius: 30}}
                    color="primary"
                    onClick={handleSaveFieldEdit}
                    disabled={saving}
                    endIcon={saving ? <CircularProgress size={20} /> : <ArrowForwardIos style={{ fontSize: 15 }} />}
                  >
                    {saving ? "Saving..." : "Save changes"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Modal>
    </>
  );
};

export default DigitizedCommercialInvoice;
